import { API, graphqlOperation } from "aws-amplify";

import { getInvoicePaymentState } from "../../../graphql/invoice/invoice-queries";
import { updateInvoiceMini } from "../../../graphql/invoice/invoice-mutations";

import {
  buildVersionedUpdate,
  isConditionFailure,
  planFormSave,
} from "./invoicePaymentUtils";

// One retry: a second conflict in a row means the invoice is busy, and the
// person should look at it again before saving.
const MAX_ATTEMPTS = 2;

export const readInvoicePaymentState = async (invoiceId) => {
  const res = await API.graphql(
    graphqlOperation(getInvoicePaymentState, { id: invoiceId }),
  );
  const invoice = res?.data?.getInvoice;
  if (!invoice) throw new Error("Invoice not found.");
  return invoice;
};

const writeInvoice = async (mutation, input, condition) => {
  // Amplify rejects with { data, errors } when AppSync returns errors, but a
  // resolved response can carry them too.
  const res = await API.graphql(
    graphqlOperation(mutation, { input, condition }),
  );
  if (res?.errors?.length || !res?.data?.updateInvoice?.id) throw res;
  return res.data.updateInvoice;
};

/**
 * Read, change and write one invoice's status, balance or payments without
 * losing a write that lands in between. `plan(invoice)` returns { update } to
 * write or { skip } to stop. The write only succeeds while the invoice's
 * updatedAt still matches the read; when it doesn't, the invoice is read again
 * and the plan runs once more on the new state.
 *
 * Resolves { invoice, update, saved } after a write (`saved` is the record the
 * mutation returned), { invoice, skip } when the plan skipped, or
 * { invoice, skip: "conflict" } when the invoice kept changing. Other errors
 * are thrown. `mutation` must be an updateInvoice mutation; pass one with a
 * larger selection when the caller needs more of the saved record back.
 */
export const saveInvoiceChange = async (
  invoiceId,
  plan,
  { mutation = updateInvoiceMini } = {},
) => {
  let previous = null;

  for (let attempt = 1; ; attempt++) {
    const invoice = await readInvoicePaymentState(invoiceId);

    // The condition also carries the yard-group check. If the invoice hasn't
    // changed since the refused write, the refusal was not a conflict.
    if (previous && invoice.updatedAt === previous.invoice.updatedAt) {
      throw previous.error;
    }

    const result = plan(invoice) || {};
    if (!result.update) return { invoice, skip: result.skip };

    const { input, condition } = buildVersionedUpdate(invoice, result.update);
    try {
      const saved = await writeInvoice(mutation, input, condition);
      return { invoice, update: result.update, saved };
    } catch (error) {
      if (!isConditionFailure(error)) throw error;
      if (attempt >= MAX_ATTEMPTS) return { invoice, skip: "conflict" };
      previous = { invoice, error };
    }
  }
};

export const CONFLICT_MESSAGE =
  "Someone else changed this invoice while you were saving. Nothing was saved; check it and try again.";

export const VOIDED_MESSAGE =
  "The invoice was voided while you were saving. Nothing was saved.";

// Marked userFacing so the data provider's retry-once wrapper doesn't run the
// save again, and so the form can show the message instead of a generic error.
const userFacingError = (message) =>
  Object.assign(new Error(message), { userFacing: true });

/**
 * The invoice form's save, behind the data provider's update. Same version
 * check and single retry as the other invoice writes, written with the full
 * updateInvoice mutation because the form's onSuccess needs the saved record
 * (account and payment term for Zapier, event and updatedAt for line items).
 */
export const saveInvoiceForm = async (formInput, mutation) => {
  const result = await saveInvoiceChange(
    formInput.id,
    planFormSave(formInput),
    { mutation },
  );
  if (result.skip === "void") throw userFacingError(VOIDED_MESSAGE);
  if (result.skip) throw userFacingError(CONFLICT_MESSAGE);
  return result.saved;
};
